import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Phone,
  Plus,
  Receipt,
  Search,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectNoneItem,
  SelectTrigger,
  SelectValue,
  clearable,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DocumentNumberLink from "@/components/DocumentNumberLink";
import ListFilterBar from "@/components/ListFilterBar";
import Pagination from "@/components/Pagination";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import CustomerLedgerPrint, {
  type LedgerPrintData,
} from "@/components/CustomerLedgerPrint";
import {
  useClearFilterParams,
  useFilterParam,
} from "@/hooks/useFilterParam";
import { formatLedgerBalance, formatNumber } from "@/lib/format";
import { renderReactToPdf, safePdfFilename } from "@/lib/htmlPdf";
import type { PdfCompany } from "@/lib/pdf";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CustomerOption {
  id: number;
  code: string;
  name: string;
  phone: string;
  email: string;
  balance: number;
  last_payment_date: string | null;
}

interface LedgerRow {
  id: number;
  transaction_date: string;
  reference_no: string;
  transaction_type: string;
  description: string;
  debit: number;
  credit: number;
  running_balance: number;
  source_module: string;
  source_id: number | null;
  created_by_name: string | null;
  modified_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface LedgerResponse {
  customer: {
    id: number;
    code: string;
    name: string;
    phone: string;
    email: string;
    address: string;
  };
  filters: {
    from: string | null;
    to: string | null;
    type: string | null;
    ref: string | null;
    outstanding_only: boolean;
  };
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
  summary: {
    current_balance: number;
    total_invoiced: number;
    total_paid: number;
    invoice_count: number;
    payment_count: number;
    last_payment_date: string | null;
  };
  aging: {
    current: number;
    d30: number;
    d60: number;
    d90: number;
    total: number;
  };
  rows: LedgerRow[];
}

const TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "invoice", label: "Sales Invoice" },
  { value: "payment", label: "Payment" },
  { value: "credit_note", label: "Credit Note" },
  { value: "opening_balance", label: "Opening Balance" },
  { value: "refund", label: "Refund" },
  { value: "adjustment", label: "Adjustment" },
  { value: "reversal", label: "Reversal" },
];

const MANUAL_TYPES = [
  { value: "opening_balance", label: "Opening Balance (Debit)" },
  { value: "refund", label: "Refund to Customer (Debit)" },
  { value: "credit_note", label: "Credit Note (Credit)" },
  { value: "adjustment", label: "Manual Adjustment" },
];

function typeLabel(t: string): string {
  return TYPE_OPTIONS.find((o) => o.value === t)?.label ?? t.replace(/_/g, " ");
}

function formatDmy(input?: string | null) {
  if (!input) return "—";
  const s = String(input);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

function rowDocLink(row: LedgerRow): { to: string; canOpen: boolean } | null {
  if (row.source_module === "sales_invoice" && row.source_id) {
    return { to: `/sales-invoices/${row.source_id}`, canOpen: true };
  }
  if (row.source_module === "receipt" && row.source_id) {
    return { to: `/receipts/${row.source_id}`, canOpen: true };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function FinanceCustomerLedger() {
  const { user, can } = useAuth();
  const canWrite = can("customer_ledger.write");
  const canOpenInvoice = can("sales_invoices.read");
  const canOpenReceipt = can("receipts.read");
  const canRecordPayment = can("receipts.write");

  const [searchParams, setSearchParams] = useSearchParams();
  const customerIdParam = searchParams.get("customer_id") ?? "";
  const selectedCustomerId = customerIdParam
    ? parseInt(customerIdParam, 10)
    : null;

  const [dateFrom, setDateFrom] = useFilterParam("date_from");
  const [dateTo, setDateTo] = useFilterParam("date_to");
  const [typeFilter, setTypeFilter] = useFilterParam("type");
  const [refFilter, setRefFilter] = useFilterParam("ref");
  const [outstandingFilter, setOutstandingFilter] = useFilterParam("outstanding");
  const [pageParam, setPageParam] = useFilterParam("page");
  const clearFilters = useClearFilterParams([
    "date_from",
    "date_to",
    "type",
    "ref",
    "outstanding",
    "page",
  ]);

  const page = Math.max(1, parseInt(pageParam || "1", 10) || 1);

  // Customer search / autocomplete
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerSearchRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<LedgerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState<PdfCompany>({});

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualForm, setManualForm] = useState({
    transaction_type: "opening_balance",
    amount: "",
    transaction_date: new Date().toISOString().slice(0, 10),
    direction: "debit" as "debit" | "credit",
    reference_no: "",
    description: "",
  });

  const currencySymbol = (company.currency_symbol || "GH₵").trim() || "GH₵";
  const activeType = typeFilter || "all";
  const outstandingOnly = outstandingFilter === "1";

  const hasAnyFilter = Boolean(
    dateFrom || dateTo || typeFilter || refFilter || outstandingOnly,
  );

  // Load company branding for PDF/statement
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ settings: Record<string, string> }>(
          "/settings",
        );
        const s = res.settings ?? {};
        setCompany({
          name: s["company.name"] || undefined,
          email: s["company.email"] || undefined,
          phone: s["company.phone"] || undefined,
          address: s["company.address"] || undefined,
          currency_code: s["currency.code"] || undefined,
          currency_symbol: s["currency.symbol"] || undefined,
        });
      } catch {
        // Non-fatal
      }
    })();
  }, []);

  // Debounced customer search
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearchingCustomers(true);
      try {
        const qs = customerQuery.trim()
          ? `?q=${encodeURIComponent(customerQuery.trim())}`
          : "";
        const res = await api.get<{ customers: CustomerOption[] }>(
          `/customer-ledger/customers${qs}`,
        );
        if (!cancelled) setCustomerOptions(res.customers ?? []);
      } catch {
        if (!cancelled) setCustomerOptions([]);
      } finally {
        if (!cancelled) setSearchingCustomers(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [customerQuery]);

  // Close customer dropdown on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (
        customerSearchRef.current &&
        !customerSearchRef.current.contains(e.target as Node)
      ) {
        setShowCustomerDropdown(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const selectCustomer = useCallback(
    (c: CustomerOption) => {
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          sp.set("customer_id", String(c.id));
          sp.delete("page");
          return sp;
        },
        { replace: true },
      );
      setCustomerQuery(c.name);
      setShowCustomerDropdown(false);
    },
    [setSearchParams],
  );

  const loadLedger = useCallback(async () => {
    if (!selectedCustomerId || selectedCustomerId <= 0) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("page_size", "10");
      if (dateFrom) qs.set("from", dateFrom);
      if (dateTo) qs.set("to", dateTo);
      if (typeFilter) qs.set("type", typeFilter);
      if (refFilter) qs.set("ref", refFilter);
      if (outstandingOnly) qs.set("outstanding_only", "1");
      const res = await api.get<LedgerResponse>(
        `/customer-ledger/${selectedCustomerId}?${qs.toString()}`,
      );
      setData(res);
      if (res.customer?.name) setCustomerQuery(res.customer.name);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load customer ledger");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    selectedCustomerId,
    page,
    dateFrom,
    dateTo,
    typeFilter,
    refFilter,
    outstandingOnly,
  ]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const outstanding = data?.summary.current_balance ?? 0;
  const settled = outstanding <= 0.01;

  // Build print/PDF payload from current ledger view
  const printData: LedgerPrintData | null = useMemo(() => {
    if (!data) return null;
    // Statement uses chronological order (oldest first) for the PDF table
    const chronological = [...data.rows].reverse();
    return {
      customerName: data.customer.name,
      dateFrom: data.filters.from,
      dateTo: data.filters.to,
      openingBalance: 0,
      rows: chronological.map((r) => ({
        date: r.transaction_date,
        type: r.transaction_type === "payment" ? "receipt" : "invoice",
        reference: r.reference_no,
        description: r.description || typeLabel(r.transaction_type),
        debit: r.debit,
        credit: r.credit,
        running: r.running_balance,
      })),
      totals: {
        total_debit: data.summary.total_invoiced,
        total_credit: data.summary.total_paid,
        closing_balance: data.summary.current_balance,
      },
    };
  }, [data]);

  function resolvePreparedBy(): string {
    return (
      user?.name?.trim() ||
      user?.username?.trim() ||
      user?.email?.trim() ||
      "—"
    );
  }

  async function buildPdf() {
    if (!printData) throw new Error("Ledger not loaded");
    const printCompany = {
      name: (company.name || "BillForge").toString(),
      address: (company.address || "").toString(),
      phone: (company.phone || "").toString(),
      email: (company.email || "").toString(),
      currencySymbol,
    };
    return renderReactToPdf(
      <CustomerLedgerPrint
        data={printData}
        company={printCompany}
        preparedBy={resolvePreparedBy()}
      />,
      {
        filename: safePdfFilename(
          "Statement",
          data?.customer.name || `Customer-${selectedCustomerId}`,
        ),
      },
    );
  }

  async function handlePreviewPdf() {
    if (generatingPdf || !printData) return;
    setGeneratingPdf(true);
    try {
      const pdf = await buildPdf();
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(pdf.url);
      setPdfFilename(pdf.filename);
      setPdfModalOpen(true);
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  }

  async function handleDownloadPdf() {
    if (generatingPdf || !printData) return;
    setGeneratingPdf(true);
    try {
      const pdf = await buildPdf();
      const a = document.createElement("a");
      a.href = pdf.url;
      a.download = pdf.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(pdf.url);
      toast.success("Statement downloaded");
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  }

  function handlePdfModalOpenChange(open: boolean) {
    if (!open) {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setPdfFilename("");
    }
    setPdfModalOpen(open);
  }

  function exportCsv() {
    if (!data?.rows.length) {
      toast.error("No rows to export");
      return;
    }
    const header = [
      "Date",
      "Ref No",
      "Type",
      "Description",
      "Debit",
      "Credit",
      "Balance",
      "Created By",
    ];
    const lines = data.rows.map((r) =>
      [
        r.transaction_date,
        r.reference_no,
        r.transaction_type,
        `"${(r.description || "").replace(/"/g, '""')}"`,
        r.debit > 0 ? r.debit.toFixed(2) : "",
        r.credit > 0 ? r.credit.toFixed(2) : "",
        `"${formatLedgerBalance(r.running_balance)}"`,
        r.created_by_name ?? "",
      ].join(","),
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Customer-Ledger-${data.customer.code}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  }

  async function submitManualEntry() {
    if (!selectedCustomerId) return;
    const amount = Number(manualForm.amount);
    if (!(amount > 0)) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!manualForm.transaction_type) {
      toast.error("Transaction type is required");
      return;
    }
    if (
      manualForm.transaction_type === "adjustment" &&
      !manualForm.direction
    ) {
      toast.error("Direction is required");
      return;
    }
    setManualSaving(true);
    try {
      const body: Record<string, unknown> = {
        customer_id: selectedCustomerId,
        transaction_type: manualForm.transaction_type,
        amount,
        transaction_date: manualForm.transaction_date,
        reference_no: manualForm.reference_no.trim() || undefined,
        description: manualForm.description.trim() || undefined,
      };
      if (manualForm.transaction_type === "adjustment") {
        body.direction = manualForm.direction;
      }
      await api.post("/customer-ledger/manual", body);
      toast.success("Ledger entry posted");
      setManualOpen(false);
      setManualForm((f) => ({
        ...f,
        amount: "",
        reference_no: "",
        description: "",
      }));
      await loadLedger();
    } catch (err) {
      toast.error((err as Error).message || "Failed to post entry");
    } finally {
      setManualSaving(false);
    }
  }

  function setPage(next: number) {
    setPageParam(next <= 1 ? "" : String(next));
  }

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Customer Ledger</h1>
          <p className="text-muted-foreground">
            Accounts receivable sub-ledger — track what each customer owes,
            payments received, and aging analysis.
          </p>
        </div>
        {selectedCustomerId && data ? (
          <div className="flex flex-wrap items-center gap-2">
            {canRecordPayment ? (
              <Button variant="outline" size="sm" asChild>
                <Link to="/receipts/new">
                  <Receipt className="h-4 w-4 mr-1" />
                  Record Payment
                </Link>
              </Button>
            ) : null}
            {canWrite ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setManualOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Manual Entry
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePreviewPdf}
              disabled={generatingPdf || !data}
            >
              <FileText className="h-4 w-4 mr-1" />
              {generatingPdf ? "Preparing…" : "PDF"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={!data?.rows.length}
            >
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={generatingPdf || !data}
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          </div>
        ) : null}
      </div>

      {/* Customer search */}
      <Card className="shadow-card">
        <CardContent className="pt-5">
          <div ref={customerSearchRef} className="relative max-w-xl">
            <Label htmlFor="customer-search" className="text-xs uppercase tracking-wide text-muted-foreground">
              Customer
            </Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="customer-search"
                placeholder="Search by name, phone or email…"
                value={customerQuery}
                onChange={(e) => {
                  setCustomerQuery(e.target.value);
                  setShowCustomerDropdown(true);
                }}
                onFocus={() => setShowCustomerDropdown(true)}
                className="pl-9 h-10"
                autoComplete="off"
              />
            </div>
            {showCustomerDropdown && (customerOptions.length > 0 || searchingCustomers) ? (
              <div className="absolute z-20 mt-1 w-full max-w-xl rounded-md border border-border bg-popover shadow-lg max-h-64 overflow-y-auto">
                {searchingCustomers ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Searching…
                  </div>
                ) : null}
                {customerOptions.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full text-left px-3 py-2.5 hover:bg-muted/60 border-b border-border/50 last:border-0"
                    onClick={() => selectCustomer(c)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{c.name}</span>
                      <span
                        className={`text-xs font-mono shrink-0 ${
                          c.balance > 0.01
                            ? "text-rose-600 dark:text-rose-400"
                            : "text-emerald-600 dark:text-emerald-400"
                        }`}
                      >
                        {currencySymbol} {formatLedgerBalance(c.balance)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground flex gap-2 mt-0.5">
                      <span className="font-mono">{c.code}</span>
                      {c.phone ? <span>{c.phone}</span> : null}
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {!selectedCustomerId ? (
        <Card className="shadow-card">
          <CardContent className="py-16 text-center text-muted-foreground">
            Select a customer above to view their receivables ledger.
          </CardContent>
        </Card>
      ) : null}

      {selectedCustomerId && data ? (
        <>
          {/* Summary + aging */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{data.customer.name}</CardTitle>
                <CardDescription className="flex flex-wrap gap-x-4 gap-y-1">
                  <span className="font-mono">{data.customer.code}</span>
                  {data.customer.phone ? (
                    <span className="inline-flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {data.customer.phone}
                    </span>
                  ) : null}
                  {data.customer.email ? <span>{data.customer.email}</span> : null}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">
                      Outstanding
                    </div>
                    <div
                      className={`text-2xl font-bold font-mono mt-1 ${
                        settled
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-rose-600 dark:text-rose-400"
                      }`}
                    >
                      {currencySymbol} {formatLedgerBalance(outstanding)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">
                      Total Invoiced
                    </div>
                    <div className="text-xl font-semibold font-mono mt-1">
                      {currencySymbol} {formatNumber(data.summary.total_invoiced)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {data.summary.invoice_count} invoice(s)
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">
                      Total Paid
                    </div>
                    <div className="text-xl font-semibold font-mono mt-1 text-emerald-600 dark:text-emerald-400">
                      {currencySymbol} {formatNumber(data.summary.total_paid)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {data.summary.payment_count} payment(s)
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground">
                      Last Payment
                    </div>
                    <div className="text-xl font-semibold mt-1">
                      {formatDmy(data.summary.last_payment_date)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                  Aging Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {(
                  [
                    ["Current (0–30d)", data.aging.current, "text-cyan-700 dark:text-cyan-400"],
                    ["31–60 days", data.aging.d30, "text-amber-700 dark:text-amber-400"],
                    ["61–90 days", data.aging.d60, "text-orange-700 dark:text-orange-400"],
                    ["90+ days", data.aging.d90, "text-rose-700 dark:text-rose-400"],
                  ] as const
                ).map(([label, amt, tone]) => (
                  <div key={label} className="flex justify-between items-center">
                    <span className="text-muted-foreground">{label}</span>
                    <span className={`font-mono font-medium ${tone}`}>
                      {currencySymbol} {formatNumber(amt)}
                    </span>
                  </div>
                ))}
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Total Due</span>
                  <span className="font-mono">
                    {currencySymbol} {formatNumber(data.aging.total)}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters + table */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
            <div className="space-y-2">
              <ListFilterBar
                title="Filter Ledger"
                searchValue={refFilter}
                onSearchChange={setRefFilter}
                searchPlaceholder="Invoice / ref #"
                dateFrom={{ value: dateFrom, onChange: setDateFrom }}
                dateTo={{ value: dateTo, onChange: setDateTo }}
                hasAnyFilter={hasAnyFilter}
                onClear={clearFilters}
              />
              <div className="overflow-hidden rounded-md border border-border bg-cyan-50/30 dark:bg-cyan-950/10 shadow-card p-3 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Transaction Type
                </Label>
                <Select
                  value={activeType}
                  onValueChange={clearable((v) =>
                    setTypeFilter(v === "all" ? "" : v),
                  )}
                >
                  <SelectTrigger className="h-9 bg-background">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectNoneItem />
                    {TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={outstandingOnly}
                    onChange={(e) =>
                      setOutstandingFilter(e.target.checked ? "1" : "")
                    }
                    className="rounded border-border"
                  />
                  Debits only (invoices / charges)
                </label>
              </div>
            </div>

            <Card className="shadow-card overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 sticky top-0">
                      <TableHead className="w-28">Date</TableHead>
                      <TableHead className="w-32">Ref No</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right w-28">DR</TableHead>
                      <TableHead className="text-right w-28">CR</TableHead>
                      <TableHead className="text-right w-28">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center py-10 text-muted-foreground"
                        >
                          Loading…
                        </TableCell>
                      </TableRow>
                    ) : data.rows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center py-10 text-muted-foreground"
                        >
                          No transactions match these filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.rows.map((r) => {
                        const link = rowDocLink(r);
                        const canOpen =
                          link &&
                          (r.source_module === "sales_invoice"
                            ? canOpenInvoice
                            : canOpenReceipt);
                        return (
                          <TableRow
                            key={r.id}
                            className="hover:bg-muted/30 transition-colors"
                          >
                            <TableCell className="whitespace-nowrap">
                              {formatDmy(r.transaction_date)}
                            </TableCell>
                            <TableCell>
                              {link && r.reference_no ? (
                                <DocumentNumberLink
                                  to={link.to}
                                  canOpen={!!canOpen}
                                >
                                  {r.reference_no}
                                </DocumentNumberLink>
                              ) : (
                                <span className="font-mono text-muted-foreground">
                                  {r.reference_no || "—"}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                <span>{r.description || typeLabel(r.transaction_type)}</span>
                                <Badge variant="outline" className="w-fit text-[10px] capitalize">
                                  {typeLabel(r.transaction_type)}
                                </Badge>
                                {r.created_by_name ? (
                                  <span className="text-[10px] text-muted-foreground">
                                    by {r.created_by_name}
                                    {r.created_at
                                      ? ` · ${formatDmy(r.created_at.slice(0, 10))}`
                                      : ""}
                                  </span>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono text-rose-600/90 dark:text-rose-400/90">
                              {r.debit > 0 ? formatNumber(r.debit) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-emerald-600 dark:text-emerald-400">
                              {r.credit > 0 ? formatNumber(r.credit) : "—"}
                            </TableCell>
                            <TableCell
                              className={`text-right font-mono font-semibold ${
                                r.running_balance <= 0.01
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : ""
                              }`}
                            >
                              {formatLedgerBalance(r.running_balance)}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
              {data.pagination.total > 0 ? (
                <Pagination
                  page={data.pagination.page}
                  pageCount={data.pagination.total_pages}
                  total={data.pagination.total}
                  from={
                    (data.pagination.page - 1) * data.pagination.page_size + 1
                  }
                  to={Math.min(
                    data.pagination.page * data.pagination.page_size,
                    data.pagination.total,
                  )}
                  onPrev={() => setPage(page - 1)}
                  onNext={() => setPage(page + 1)}
                />
              ) : null}
            </Card>
          </div>
        </>
      ) : null}

      <PdfPreviewModal
        open={pdfModalOpen}
        onOpenChange={handlePdfModalOpenChange}
        pdfUrl={pdfPreviewUrl}
        filename={pdfFilename}
        title="Customer Statement"
        documentNumber={data?.customer.name}
      />

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manual Ledger Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Type</Label>
              <Select
                value={manualForm.transaction_type || undefined}
                onValueChange={clearable((v) =>
                  setManualForm((f) => ({ ...f, transaction_type: v })),
                )}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectNoneItem />
                  {MANUAL_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {manualForm.transaction_type === "adjustment" ? (
              <div className="space-y-1">
                <Label>Direction</Label>
                <Select
                  value={manualForm.direction || undefined}
                  onValueChange={clearable((v) =>
                    setManualForm((f) => ({
                      ...f,
                      direction: v as "debit" | "credit",
                    })),
                  )}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select direction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectNoneItem />
                    <SelectItem value="debit">Debit (increase owed)</SelectItem>
                    <SelectItem value="credit">Credit (reduce owed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Amount ({currencySymbol})</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualForm.amount}
                  onChange={(e) =>
                    setManualForm((f) => ({ ...f, amount: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={manualForm.transaction_date}
                  onChange={(e) =>
                    setManualForm((f) => ({
                      ...f,
                      transaction_date: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Reference (optional)</Label>
              <Input
                value={manualForm.reference_no}
                onChange={(e) =>
                  setManualForm((f) => ({ ...f, reference_no: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Description (optional)</Label>
              <Input
                value={manualForm.description}
                onChange={(e) =>
                  setManualForm((f) => ({ ...f, description: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitManualEntry} disabled={manualSaving}>
              {manualSaving ? "Posting…" : "Post Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
